import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useLanguageStore } from '../../stores/languageStore';
import { useAuthStore } from '../../stores/authStore';
import LoadingSpinner from '../ui/LoadingSpinner';
import FreeHTMLEditor from '../ui/FreeHTMLEditor';
import DocumentPreviewPage from '../ui/DocumentPreviewPage';
import { PhoneVerification } from '../PhoneVerification';
import axios from 'axios';

import { useNavigate } from 'react-router-dom';

interface DocumentGenerationStepProps {
  lawsuitId: string;
  onBack: () => void;
  onDocumentsGenerated: (documents: DocumentTemplate[]) => void;
  isPremiumMode?: boolean;
  onUpgradeToPremium?: () => void;
  isEditMode?: boolean;
}

interface DocumentTemplate {
  id: string;
  name: string;
  description: string;
  type: 'complaint' | 'lawsuit' | 'demand_letter' | 'settlement_agreement' | 'discovery_request';
  recommended: boolean;
  estimatedPages: number;
  isGenerated: boolean;
  generatedAt?: Date;
  downloadUrl?: string;
  content?: string;
  language?: string;
}

const DocumentGenerationStep: React.FC<DocumentGenerationStepProps> = ({
  lawsuitId,
  onBack,
  onDocumentsGenerated,
  isPremiumMode = false,
  onUpgradeToPremium,
  isEditMode = false,
}) => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useLanguageStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const MAX_DOCS_NON_ADMIN = 2;

  // Helper function to get the correct legal URL based on current language
  const getLegalUrl = (type: 'terms' | 'privacy') => {
    const currentLanguage = i18n.language || 'en';
    return `/legal/${type}-${currentLanguage}.html`;
  };

  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [generatedDocuments, setGeneratedDocuments] = useState<DocumentTemplate[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentlyGenerating, setCurrentlyGenerating] = useState<string | null>(null);
  const [editingDocument, setEditingDocument] = useState<DocumentTemplate | null>(null);
  const [previewingDocument, setPreviewingDocument] = useState<{content: string, originalName: string} | null>(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [disclaimerShownForSession, setDisclaimerShownForSession] = useState(false);
  const [showPremiumUpgrade, setShowPremiumUpgrade] = useState(false);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);

  // Get available document templates
  const [availableDocuments] = useState<DocumentTemplate[]>([
    {
      id: 'lawsuit',
      name: t('documents.lawsuit.name'),
      description: t('documents.lawsuit.description'),
      type: 'lawsuit',
      recommended: true,
      estimatedPages: 6,
      isGenerated: false,
    },
    {
      id: 'complaint',
      name: t('documents.complaint.name'),
      description: t('documents.complaint.description', 'A formal complaint/notice to the accused party. This should be the first step before considering a lawsuit, giving the other party a chance to resolve the issue amicably.'),
      type: 'complaint',
      recommended: false,
      estimatedPages: 1,
      isGenerated: false,
    },
    {
      id: 'demand_letter',
      name: t('documents.demandLetter.name'),
      description: t('documents.demandLetter.description'),
      type: 'demand_letter',
      recommended: true,
      estimatedPages: 2,
      isGenerated: false,
    },

    {
      id: 'discovery_request',
      name: t('documents.discoveryRequest.name'),
      description: t('documents.discoveryRequest.description'),
      type: 'discovery_request',
      recommended: false,
      estimatedPages: 3,
      isGenerated: false,
    },
  ]);

  React.useEffect(() => {
    // Auto-select recommended documents
    const recommendedDocs = availableDocuments
      .filter(doc => doc.recommended)
      .map(doc => doc.id);
    setSelectedDocuments(recommendedDocs);
  }, [availableDocuments]);

  React.useEffect(() => {
    if (generatedDocuments.length > 0 && !isGenerating && !disclaimerShownForSession) {
      setShowDisclaimer(true);
      setDisclaimerChecked(false);
      setDisclaimerShownForSession(true);
    }
  }, [generatedDocuments, isGenerating, disclaimerShownForSession]);

  // Note: generateDocumentsMutation was replaced with direct API calls in handleGenerateDocuments

  const handleDocumentSaved = (updatedDocument: any) => {
    // Convert the updated document to DocumentTemplate format
    const updatedTemplate: DocumentTemplate = {
      id: updatedDocument.id,
      name: updatedDocument.originalName,
      description: updatedDocument.description || '',
      type: editingDocument?.type || 'complaint',
      recommended: editingDocument?.recommended || false,
      estimatedPages: Math.ceil(updatedDocument.size / 1024), // Rough estimate
      isGenerated: true,
      generatedAt: new Date(updatedDocument.uploadedAt),
      downloadUrl: updatedDocument.url,
      content: updatedDocument.content,
      language: updatedDocument.language
    };

    // Update the generated documents list
    setGeneratedDocuments(prev => [...prev, updatedTemplate]);
    
    // Close the editor
    setEditingDocument(null);

    // Also invalidate the lawsuit query to ensure the parent component updates
    queryClient.invalidateQueries({ queryKey: ['lawsuit', lawsuitId] });
  };

  // Simplified version grouping using new properties
  const groupDocumentsByBaseName = (documents: DocumentTemplate[]) => {
    const groupedDocs: Record<string, DocumentTemplate[]> = {};
    documents.forEach(doc => {
      // Use originalName or name as the grouping key since they're now clean
      const baseName = (doc as any).originalName || doc.name;
      if (!groupedDocs[baseName]) groupedDocs[baseName] = [];
      groupedDocs[baseName].push(doc);
    });
    // Sort documents within each group by version (highest first)
    Object.keys(groupedDocs).forEach(baseName => {
      groupedDocs[baseName].sort((a, b) => {
        const aVersion = (a as any).version || 1;
        const bVersion = (b as any).version || 1;
        return bVersion - aVersion;
      });
    });
    return groupedDocs;
  };

  const handleDocumentToggle = (documentId: string) => {
    // Remove the recommended check so all documents can be toggled
    setSelectedDocuments(prev => {
      const isCurrentlySelected = prev.includes(documentId);
      if (isCurrentlySelected) {
        return prev.filter(id => id !== documentId);
      } else {
        return [...prev, documentId];
      }
    });
  };

  const handlePremiumUpgrade = () => {
    setShowPremiumUpgrade(false);
    if (onUpgradeToPremium) {
      onUpgradeToPremium();
    }
  };

  const handleGenerateDocuments = async () => {
    if (selectedDocuments.length === 0) return;
    
    // Check if phone verification is required
    if (!user?.phoneVerified) {
      // Refresh user data before opening phone verification modal
      try {
        await useAuthStore.getState().refreshUser();
      } catch (error) {
        console.error('Failed to refresh user data:', error);
      }
      setShowPhoneVerification(!user?.phoneVerified);
      return;
    }
    
    // Proceed with document generation
    await generateDocuments();
  };

  const generateDocuments = async () => {
    setIsGenerating(true);
    try {
      // Send all selected document types in one request
      const response = await axios.post('/api/ai/generate-documents', {
        lawsuitId: lawsuitId,
        documentTypes: selectedDocuments.map(docId => {
          const doc = availableDocuments.find(d => d.id === docId);
          return doc ? doc.type : docId;
        }),
        isPremiumMode: isPremiumMode
      });
      const docs = response.data?.data?.documents || [];
      const savedDocs = response.data?.data?.savedDocuments || [];
      
      // Map API response to DocumentTemplate[] using saved documents (which have real database IDs)
      const generatedDocs: DocumentTemplate[] = savedDocs.map((savedDoc: any) => {
        // Find the corresponding generated document for content
        const generatedDoc = docs.find((doc: any) => doc.documentType === savedDoc.type);
        const template = availableDocuments.find(d => d.type === savedDoc.type);
        
        return {
          id: savedDoc.id, // Use the real database ID
          name: template?.name || savedDoc.originalName || savedDoc.filename,
          description: `${template?.description || ''}${isPremiumMode ? ' (Premium)' : ''}`,
          type: savedDoc.type,
          recommended: template?.recommended || false,
          estimatedPages: savedDoc.content ? Math.ceil(savedDoc.content.length / 2000) : 1,
          isGenerated: true,
          generatedAt: new Date(savedDoc.uploadedAt),
          downloadUrl: savedDoc.url || '#',
          content: savedDoc.content || generatedDoc?.content || '',
          language: savedDoc.language || 'en'
        };
      });
      setGeneratedDocuments(generatedDocs);
      setCurrentlyGenerating(null);
      queryClient.invalidateQueries({ queryKey: ['lawsuit', lawsuitId] });
      if (onDocumentsGenerated) {
        onDocumentsGenerated(generatedDocs);
      }
    } catch (error) {
      console.error('Document generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePhoneVerificationComplete = async (_phoneNumber: string) => {
    setShowPhoneVerification(false);
    
    // Refresh user data after phone verification to get updated phoneVerified status
    try {
      await useAuthStore.getState().refreshUser();
    } catch (error) {
      console.error('Failed to refresh user data after phone verification:', error);
    }
    
    // Proceed with document generation after phone verification
    generateDocuments();
  };

  const handlePhoneVerificationCancel = () => {
    setShowPhoneVerification(false);
  };





  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'complaint':
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        );
      case 'lawsuit':
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'demand_letter':
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
          </svg>
        );

      case 'discovery_request':
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const handleSaveLawsuit = async () => {
    try {
      // The lawsuit is already saved with the generated documents
      // We just need to ensure the status is updated if needed
      await axios.patch(`/api/lawsuits/${lawsuitId}`, {
        status: 'pending_payment'
      });
    } catch (error) {
      console.error('Error saving lawsuit:', error);
      // Continue with navigation even if save fails
    }
  };

  const redirectToEditPage = () => {
    // Navigate to the lawsuit detail page where users can view and edit
    navigate(`/lawsuit/${lawsuitId}`);
  };

  // Compute which document types have already been generated
  const generatedTypes = new Set(generatedDocuments.map(doc => doc.type));
  const notYetGeneratedDocuments = availableDocuments.filter(doc => !generatedTypes.has(doc.type));

  // If all documents are generated, do not show the selection UI or generate button
  const allGenerated = notYetGeneratedDocuments.length === 0;

  return (
    <>
      <style>{`
        .document-preview {
          background: white;
          padding: 20px;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .document-preview .court-header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #000;
          padding-bottom: 15px;
        }
        .document-preview .court-name {
          font-size: 16pt;
          font-weight: bold;
          margin-bottom: 8px;
        }
        .document-preview .case-caption {
          text-align: center;
          margin-bottom: 30px;
          font-size: 12pt;
          font-weight: bold;
        }
        .document-preview .section-title {
          font-size: 12pt;
          font-weight: bold;
          margin-top: 25px;
          margin-bottom: 12px;
          text-decoration: underline;
        }
        .document-preview .subsection-title {
          font-size: 11pt;
          font-weight: bold;
          margin-top: 18px;
          margin-bottom: 8px;
        }
        .document-preview .paragraph {
          margin-bottom: 12px;
          text-align: justify;
        }
        .document-preview .indented {
          margin-left: 25px;
        }
        .document-preview .numbered-list {
          margin-left: 25px;
        }
        .document-preview .numbered-list li {
          margin-bottom: 8px;
        }
        .document-preview .signature-section {
          margin-top: 40px;
          text-align: center;
        }
        .document-preview .signature-line {
          border-top: 1px solid #000;
          width: 150px;
          display: inline-block;
          margin-top: 25px;
        }
        .document-preview .footer {
          margin-top: 30px;
          font-size: 9pt;
          text-align: center;
          color: #666;
        }
      `}</style>
      <div className={`space-y-6 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {isEditMode 
            ? t('filing.documentGeneration.editTitle') 
            : t('filing.documentGeneration.title')
          }
        </h2>
        <p className="text-gray-600 mb-6">
          {isEditMode 
            ? t('filing.documentGeneration.editSubtitle')
            : t('filing.documentGeneration.subtitle')
          }
        </p>
        {isEditMode && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
                             <div>
                 <h3 className="text-sm font-semibold text-blue-900 mb-1">
                   {t('filing.documentGeneration.regeneratingTitle')}
                 </h3>
                 <p className="text-sm text-blue-800">
                   {t('filing.documentGeneration.regeneratingMessage')}
                 </p>
               </div>
            </div>
          </div>
        )}
      </div>

      {/* Premium Mode Banner */}
      {isPremiumMode && (
        <div className="mb-6 p-4 bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-6 h-6 text-purple-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-purple-900">
                {t('filing.documentGeneration.premiumMode', 'Premium Mode Active')}
              </h3>
              <p className="text-purple-700">
                {t('filing.documentGeneration.premiumModeDescription', 'Enhanced AI models and unlimited document generation')}
              </p>
            </div>
          </div>
          {generatedDocuments.length > 0 && (
            <button
              onClick={handleGenerateDocuments}
              className="mt-3 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors min-h-[44px]"
            >
              {t('filing.documentGeneration.regenerateInPremium', 'Regenerate All in Premium Mode')}
            </button>
          )}
        </div>
      )}

      {/* Document Selection UI (only if not all generated) */}
      {!isGenerating && generatedDocuments.length === 0 && !allGenerated && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              {t('filing.documentGeneration.selectDocuments')}
            </h3>
            {!user?.isAdmin && (
              <div className="text-sm text-gray-600">
                {t('filing.documentGeneration.documentLimit', 'Up to {{count}} documents for non-admin users', { count: MAX_DOCS_NON_ADMIN })}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {notYetGeneratedDocuments.map(document => (
              <div
                key={document.id}
                className={`relative border-2 rounded-lg p-4 sm:p-6 cursor-pointer transition-all min-h-[120px] ${
                  selectedDocuments.includes(document.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${document.recommended ? 'ring-2 ring-green-200' : ''}`}
                onClick={() => handleDocumentToggle(document.id)}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className={`p-2 rounded-lg ${
                      selectedDocuments.includes(document.id)
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {getDocumentIcon(document.type)}
                    </div>
                  </div>
                  
                  <div className="ml-4 flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-medium text-gray-900">
                        {document.name}
                        {document.recommended && (
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {t('common.recommended')}
                          </span>
                        )}
                      </h4>
                      
                      <input
                        type="checkbox"
                        checked={selectedDocuments.includes(document.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        readOnly
                      />
                    </div>
                    
                    <p className="mt-1 text-sm text-gray-600">
                      {document.description}
                    </p>
                    
                    <div className="mt-2 flex items-center text-xs text-gray-500">
                      <span>{t('filing.documentGeneration.estimatedPages')}: {document.estimatedPages}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-6">
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              {t('common.back')}
            </button>

            <button
              onClick={handleGenerateDocuments}
              disabled={selectedDocuments.length === 0}
              className={`px-6 py-2 rounded-md font-medium ${
                selectedDocuments.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {t('filing.documentGeneration.generateDocuments')} ({selectedDocuments.length})
            </button>
          </div>
        </div>
      )}

      {/* If all documents are generated, show a message instead */}
      {allGenerated && (
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('filing.documentGeneration.allDocumentsGenerated', 'All available documents have been generated for this case.')}
          </h3>
        </div>
      )}

      {/* Generation Progress */}
      {isGenerating && (
        <div className="text-center py-12">
          <LoadingSpinner />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {t('filing.documentGeneration.generating')}
          </h3>
          <p className="mt-2 text-gray-600">
            {currentlyGenerating && (
              <>
                {t('filing.documentGeneration.currentlyGenerating')}: {' '}
                {availableDocuments.find(doc => doc.id === currentlyGenerating)?.name}
              </>
            )}
          </p>
          
          {/* Progress bar */}
          <div className="mt-6 max-w-md mx-auto">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${(generatedDocuments.length / selectedDocuments.length) * 100}%` 
                }}
              />
            </div>
            <p className="mt-2 text-sm text-gray-600">
              {generatedDocuments.length} / {selectedDocuments.length} {t('filing.documentGeneration.documentsCompleted')}
            </p>
          </div>
        </div>
      )}

      {/* Generated Documents */}
      {generatedDocuments.length > 0 && !isGenerating && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              {t('filing.documentGeneration.generatedDocuments')}
            </h3>
                         <button
               onClick={() => {
                 // Preview first document (user can navigate through them)
                 if (generatedDocuments.length > 0) {
                   setPreviewingDocument({
                     content: generatedDocuments[0].content || '',
                     originalName: `${generatedDocuments.length} Documents - ${generatedDocuments[0].name}`
                   });
                 }
               }}
               className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 min-h-[44px] max-h-[44px] transition-all"
             >
               <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
               </svg>
               <span className="truncate">{t('common.previewAll', 'Preview All')}</span>
             </button>
          </div>
          
          <div className="space-y-4">
            {Object.entries(groupDocumentsByBaseName(generatedDocuments)).map(([baseName, docs]) => (
              <div key={baseName} className="border border-gray-200 rounded-lg overflow-hidden">
                {docs.map((document, index) => {
                  const isLatest = index === 0;
                  const versionNumber = (document as any).version || 1;
                  const isEdited = ((document as any).version || 1) > 1 || document.description?.includes('Edited version');
                  
                  return (
                    <div
                      key={document.id}
                      className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:p-6 gap-4 ${index > 0 ? 'border-t border-gray-100' : ''} ${isLatest ? 'bg-green-50' : 'bg-gray-50'} hover:bg-opacity-75 transition-colors`}
                    >
                      <div className="flex items-center">
                        <div className="flex flex-col items-center mr-4">
                          <div className={`p-2 rounded-lg ${isLatest ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                            {getDocumentIcon(document.type)}
                          </div>
                          {docs.length > 1 && (
                            <span className={`text-xs font-medium mt-1 px-1.5 py-0.5 rounded ${isLatest ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                              v{versionNumber}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center">
                            <h4 className={`text-lg font-medium truncate ${isLatest ? 'text-green-900' : 'text-gray-700'}`}>
                              {document.name}
                            </h4>
                            {isLatest && (
                              <span className="ml-2 px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium">
                                {t('common.latest', 'Latest')}
                              </span>
                            )}
                            {isEdited && (
                              <span className="ml-2 px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs font-medium">
                                {t('common.edited', 'Edited')}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center text-sm text-gray-600 mt-1">
                            <span>{t('filing.documentGeneration.generatedAt')}: {document.generatedAt?.toLocaleString()}</span>
                            <span className="mx-2">•</span>
                            <span>{document.estimatedPages} {t('common.pages', 'pages')}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {t('common.generated')}
                        </span>
                        
                        <div className="flex items-center space-x-2 w-full sm:w-auto">
                          <button
                            onClick={() => setPreviewingDocument({
                              content: document.content || '',
                              originalName: document.name
                            })}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 min-h-[44px] max-h-[44px] transition-all"
                            title={t('common.previewDownload', 'Preview & Download')}
                          >
                            <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="truncate">{t('common.preview')}</span>
                          </button>
                          <button
                            onClick={() => setEditingDocument(document)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 min-h-[44px] max-h-[44px] transition-all"
                          >
                            <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span className="truncate">{t('common.edit')}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          
          {/* Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-blue-900">
                  {t('filing.documentGeneration.ready')}
                </h4>
                <p className="text-sm text-blue-700">
                  {t('filing.documentGeneration.readyMessage', { count: generatedDocuments.length })}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              {t('common.back')}
            </button>

            <button
              onClick={async () => {
                await handleSaveLawsuit(); // Implement this to save the lawsuit
                redirectToEditPage(); // Implement this to redirect to the edit interface
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {t('filing.documentGeneration.saveAndEdit')}
            </button>
          </div>
        </div>
      )}


      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            {/* Header */}
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="ml-3 text-xl font-bold text-gray-900">{t('filing.documentGeneration.disclaimerTitle', 'Important Notice')}</h2>
            </div>
            
            {/* Disclaimer Text */}
            <div className="mb-6 text-sm text-gray-700 leading-relaxed">
              <Trans 
                i18nKey="filing.documentGeneration.disclaimerText"
                components={[
                  <a 
                    key="terms-link"
                    href={getLegalUrl('terms')} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:text-blue-800 underline font-medium transition-colors duration-200"
                  />
                ]}
              />
            </div>
            
            {/* Checkbox */}
            <label className="flex items-start mb-6 cursor-pointer group rtl:flex-row-reverse">
              <input
                type="checkbox"
                checked={disclaimerChecked}
                onChange={e => setDisclaimerChecked(e.target.checked)}
                className="mt-1 mr-3 rtl:mr-0 rtl:ml-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors duration-200 flex-shrink-0"
              />
              <div className="text-sm text-gray-700 leading-relaxed rtl:text-right">
                <Trans 
                  i18nKey="filing.documentGeneration.disclaimerAcknowledge"
                  components={[
                    <a 
                      key="terms-link"
                      href={getLegalUrl('terms')} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-blue-600 hover:text-blue-800 underline font-medium transition-colors duration-200"
                    />
                  ]}
                />
              </div>
            </label>
            
            {/* Action Button */}
            <div className="flex justify-end">
              <button
                className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                  disclaimerChecked 
                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5' 
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
                disabled={!disclaimerChecked}
                onClick={() => setShowDisclaimer(false)}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HTML Document Editor */}
      {editingDocument && (
        <FreeHTMLEditor
          document={{
            id: editingDocument.id,
            filename: editingDocument.name,
            originalName: editingDocument.name,
            mimeType: 'text/html',
            size: editingDocument.content?.length || 0,
            url: editingDocument.downloadUrl || '',
            uploadedBy: '',
            uploadedAt: editingDocument.generatedAt || new Date(),
            type: editingDocument.type || 'complaint',
            language: editingDocument.language as any,
            isGenerated: true,
            description: editingDocument.description,
            content: editingDocument.content || ''
          }}
          lawsuitId={lawsuitId}
          isOpen={!!editingDocument}
          onClose={() => setEditingDocument(null)}
          onSave={handleDocumentSaved}
          createNewVersion={false} // Don't create new versions from document generation screen
        />
      )}

      {/* Document Preview Page */}
      {previewingDocument && (
        <DocumentPreviewPage
          document={previewingDocument}
          isOpen={!!previewingDocument}
          onClose={() => setPreviewingDocument(null)}
        />
      )}

      {/* Premium Upgrade Modal */}
      {showPremiumUpgrade && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-4">
                <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('filing.documentGeneration.upgradeTitle', 'Upgrade to Premium')}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                {t('filing.documentGeneration.upgradeMessage', 'You\'ve reached the document limit for non-admin users. Upgrade to premium for unlimited document generation and enhanced AI models.')}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowPremiumUpgrade(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handlePremiumUpgrade}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  {t('common.upgrade', 'Upgrade')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phone Verification Modal */}
      {showPhoneVerification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {t('filing.documentGeneration.phoneVerificationRequired', 'Phone Verification Required')}
              </h2>
              <p className="text-sm text-gray-600">
                {t('filing.documentGeneration.phoneVerificationMessage', 'To generate legal documents, we need to verify your phone number for security purposes.')}
              </p>
            </div>
            
            <PhoneVerification
              onVerificationComplete={handlePhoneVerificationComplete}
              onCancel={handlePhoneVerificationCancel}
              className="mb-4"
              initialPhoneNumber={user?.phone || ''}
            />
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default DocumentGenerationStep; 